import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/api';

const AdminPayoutUserManagement = ({ onBack } = {}) => {
  const [payoutUsers, setPayoutUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [merchantDropdownOpen, setMerchantDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [form, setForm] = useState({
    username: '', email: '', password: '', fullName: '', isActive: true, merchantIds: [], payoutKey: ''
  });

  useEffect(() => {
    fetchPayoutUsers();
    fetchMerchants();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMerchantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showNotif = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchPayoutUsers = async () => {
    try {
      const res = await axios.get('/api/admin/payout-users');
      if (res.data.success) setPayoutUsers(res.data.data || []);
    } catch (e) { console.error('Failed to fetch payout users:', e); }
    finally { setLoading(false); }
  };

  const fetchMerchants = async () => {
    try {
      const res = await axios.get('/api/admin/merchants');
      if (res.data.success) {
        console.log('[Merchants]', res.data.data);
        setMerchants(res.data.data || []);
      }
    } catch (e) { console.error('Failed to fetch merchants:', e); }
  };

  const resetForm = () => {
    setForm({ username: '', email: '', password: '', fullName: '', isActive: true, merchantIds: [], payoutKey: '' });
    setEditingId(null);
    setShowForm(false);
    setMerchantDropdownOpen(false);
  };

  const openAdd = () => { resetForm(); setShowForm(true); };

  const openEdit = (user) => {
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      payoutKey: '',
      fullName: user.fullName || '',
      isActive: user.isActive !== false,
      merchantIds: user.merchantIds || []
    });
    setEditingId(user.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleMerchant = (merchantId) => {
    setForm(prev => ({
      ...prev,
      merchantIds: prev.merchantIds.includes(merchantId)
        ? prev.merchantIds.filter(id => id !== merchantId)
        : [...prev.merchantIds, merchantId]
    }));
  };

  const savePayoutUser = async () => {
    if (!form.username.trim()) { alert('Username is required'); return; }
    if (!form.email.trim()) { alert('Email is required'); return; }
    if (!editingId && !form.password.trim()) { alert('Password is required for new users'); return; }
    setSaving(true);
    try {
      const payload = {
        username: form.username,
        email: form.email,
        fullName: form.fullName,
        isActive: form.isActive,
        merchantIds: form.merchantIds
      };
      if (form.password.trim()) payload.password = form.password;
      if (editingId && form.payoutKey && form.payoutKey.trim()) payload.payoutKey = form.payoutKey.trim();

      if (editingId) {
        await axios.put(`/api/admin/payout-users/${editingId}`, payload);
        showNotif('Payout user updated successfully');
      } else {
        await axios.post('/api/admin/payout-users', payload);
        showNotif('Payout user created successfully');
      }
      resetForm();
      fetchPayoutUsers();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await axios.put(`/api/admin/payout-users/${id}/toggle-status`, {});
      showNotif(currentStatus ? 'User deactivated' : 'User activated');
      fetchPayoutUsers();
    } catch (e) { alert('Failed to toggle status: ' + e.message); }
  };

  const resetKey = async (id) => {
    if (!window.confirm('Reset payout key to current default?')) return;
    try {
      await axios.post(`/api/admin/payout-users/${id}/reset-key`, {});
      showNotif('Payout key reset successfully');
      fetchPayoutUsers();
    } catch (e) { alert('Failed to reset key: ' + e.message); }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`Delete payout user "${username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/payout-users/${id}`);
      showNotif('Payout user deleted');
      fetchPayoutUsers();
    } catch (e) { alert('Failed to delete: ' + e.message); }
  };

  const getMerchantNames = (merchantIds) => {
    if (!merchantIds || merchantIds.length === 0) return 'None';
    return merchantIds.map(id => {
      const m = merchants.find(m => m.id === id);
      if (!m) return `ID: ${id}`;
      const displayName = m.username || m.businessName || 'Unknown';
      return `${displayName} (ID: ${id})`;
    }).join(', ');
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }} role="status" aria-live="polite">Loading payout users...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {notification && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          padding: '12px 20px', borderRadius: '8px', fontWeight: '600',
          background: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          color: notification.type === 'success' ? '#155724' : '#721c24',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          {notification.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Payout User Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={openAdd}
            style={{ ...btnStyle('#007a75'), fontWeight: 'bold' }}>+ Add Payout User</button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit Payout User' : 'Add New Payout User'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Username *</label>
              <input type="text" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. payoutop1" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="e.g. operator@company.com" style={inputStyle} />
            </div>
            {!editingId && (
              <div>
                <label style={labelStyle}>Password *</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter password" style={inputStyle} />
              </div>
            )}
            {editingId && (
              <div>
                <label style={labelStyle}>New Payout Key <span style={{ color: '#767676', fontSize: '11px' }}>(leave blank to keep current)</span></label>
                <input type="text" value={form.payoutKey || ''} onChange={e => setForm({ ...form, payoutKey: e.target.value })}
                  placeholder="Leave blank to keep current" style={inputStyle} />
                <small style={{ color: '#666', fontSize: '11px' }}>This is the key the payout user enters at login</small>
              </div>
            )}
            <div>
              <label style={labelStyle}>Full Name</label>
              <input type="text" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })}
                placeholder="e.g. John Doe" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={form.isActive ? 'true' : 'false'} onChange={e => setForm({ ...form, isActive: e.target.value === 'true' })} style={inputStyle}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          {/* Merchant checkbox dropdown */}
          <div style={{ marginTop: '16px' }} ref={dropdownRef}>
            <label style={labelStyle}>Assigned Merchants</label>
            <div style={{ position: 'relative' }}>
              {/* Trigger button */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setMerchantDropdownOpen(o => !o)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setMerchantDropdownOpen(o => !o)}
                aria-haspopup="listbox"
                aria-expanded={merchantDropdownOpen}
                aria-label="Select assigned merchants"
                style={{
                  ...inputStyle, cursor: 'pointer', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center',
                  background: '#fff', userSelect: 'none'
                }}>
                <span style={{ color: form.merchantIds.length ? '#333' : '#767676' }}>
                  {form.merchantIds.length === 0
                    ? 'Select merchants...'
                    : form.merchantIds.map(id => {
                        const m = merchants.find(m => m.id === id);
                        return m ? (m.username || m.businessName) : `#${id}`;
                      }).join(', ')}
                </span>
                <span>{merchantDropdownOpen ? '▲' : '▼'}</span>
              </div>

              {/* Dropdown panel */}
              {merchantDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                  background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px',
                  overflowY: 'auto', marginTop: '2px'
                }}>
                  {merchants.length === 0 ? (
                    <div style={{ padding: '12px', color: '#767676', fontSize: '13px' }}>No merchants available</div>
                  ) : merchants.map(m => {
                    const displayName = m.username || m.businessName;
                    const checked = form.merchantIds.includes(m.id);
                    return (
                      <label key={m.id} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 14px', cursor: 'pointer', fontSize: '14px',
                        background: checked ? '#f0f7ff' : '#fff',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setForm(prev => ({
                              ...prev,
                              merchantIds: checked
                                ? prev.merchantIds.filter(id => id !== m.id)
                                : [...prev.merchantIds, m.id]
                            }));
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span>{displayName} <span style={{ color: '#767676', fontSize: '12px' }}>(ID: {m.id})</span></span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {form.merchantIds.length > 0 && (
              <small style={{ color: '#007bff', fontSize: '12px' }}>
                {form.merchantIds.length} merchant{form.merchantIds.length > 1 ? 's' : ''} selected
              </small>
            )}
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={resetForm} style={btnStyle('#6c757d')}>Cancel</button>
            <button onClick={savePayoutUser} disabled={saving} style={btnStyle('#007a75')}>
              {saving ? 'Saving...' : (editingId ? 'Update User' : 'Create User')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }} role="table">
          <caption className="sr-only">Payout Users</caption>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th scope="col" style={thStyle}>ID</th>
              <th scope="col" style={thStyle}>Username</th>
              <th scope="col" style={thStyle}>Email</th>
              <th scope="col" style={thStyle}>Full Name</th>
              <th scope="col" style={thStyle}>Status</th>
              <th scope="col" style={thStyle}>Assigned Merchants</th>
              <th scope="col" style={thStyle}>Created</th>
              <th scope="col" style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payoutUsers.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#767676' }}>
                No payout users found. Click "+ Add Payout User" to create one.
              </td></tr>
            ) : payoutUsers.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{user.id}</td>
                <td style={tdStyle}><strong>{user.username}</strong></td>
                <td style={tdStyle}>{user.email}</td>
                <td style={tdStyle}>{user.fullName || '-'}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                    background: user.isActive ? '#d4edda' : '#f8d7da',
                    color: user.isActive ? '#155724' : '#721c24' }}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontSize: '12px', maxWidth: '200px' }}>
                  {getMerchantNames(user.merchantIds)}
                </td>
                <td style={{ ...tdStyle, fontSize: '12px' }}>
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => openEdit(user)} style={smallBtn('#007a75')}>Edit</button>
                    <button onClick={() => toggleStatus(user.id, user.isActive)}
                      style={smallBtn(user.isActive ? '#dc3545' : '#28a745')}>
                      {user.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => resetKey(user.id)} style={smallBtn('#007a75')}>Reset Key</button>
                    <button onClick={() => deleteUser(user.id, user.username)} style={smallBtn('#6c757d')}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', gap: '24px', fontSize: '14px' }}>
        <span>Total: <strong>{payoutUsers.length}</strong></span>
        <span>Active: <strong style={{ color: '#28a745' }}>{payoutUsers.filter(u => u.isActive).length}</strong></span>
        <span>Inactive: <strong style={{ color: '#dc3545' }}>{payoutUsers.filter(u => !u.isActive).length}</strong></span>
      </div>
    </div>
  );
};

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' };
const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' };
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };
const btnStyle = (bg) => ({ padding: '10px 20px', background: bg, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' });
const smallBtn = (bg) => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });

export default AdminPayoutUserManagement;
